import { useRef, useState, useEffect } from "react"

const useCustomVideoPlayer = () => {
  const videoRef = useRef(null)
  const playerContainerRef = useRef(null)

  // States for the UI
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState("0:00")
  const [duration, setDuration] = useState("0:00")
  const [isMuted, setIsMuted] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [volume, setVolume] = useState(1)

  // Helper to format seconds into minutes:seconds
  const formatTime = timeInSeconds => {
    if (isNaN(timeInSeconds)) return "0:00"
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
  }

  // 1. Handle Play/Pause
  const togglePlay = () => {
    if (!videoRef.current) return

    if (isPlaying) {
      videoRef.current.pause()
    } else {
      videoRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  // 2. Update progress bar as the video plays
  const handleTimeUpdate = () => {
    if (!videoRef.current) return
    const current = videoRef.current.currentTime
    const total = videoRef.current.duration

    // Calculamos el porcentaje para la barra
    const percent = (current / total) * 100

    setProgress(percent)
    setCurrentTime(formatTime(current))
  }
  // 3. Handle click on progress bar (Seeking)
  const handleProgressChange = e => {
    if (!videoRef.current || (duration === '0:00')) return
    const manualChange = Number(e.target.value)
    videoRef.current.currentTime =
      (videoRef.current.duration / 100) * manualChange
    setProgress(manualChange)
  }
  // 4. Set duration for display when metadata is loaded
  const handleLoadedMetadata = () => {
    if (!videoRef.current) return
    console.log(typeof videoRef.current.duration)
    setDuration(formatTime(videoRef.current.duration))
  }
  // 5. Handle Mute/Unmute
  const toggleMute = () => {
    const newMuteStatus = !isMuted
    if (!videoRef.current) return
    videoRef.current.muted = newMuteStatus
    setIsMuted(newMuteStatus)

    if (!newMuteStatus && volume === 0) {
      setVolume(0.5)
      videoRef.current.volume = 0.5
    }
  }

  const handleVolumeChange = e => {
    const newVolume = parseFloat(e.target.value)
    setVolume(newVolume)
    if (!videoRef.current) return
    videoRef.current.volume = newVolume

    // Si el usuario mueve la barra, quitamos el mute
    if (newVolume > 0) {
      videoRef.current.muted = false
      setIsMuted(false)
    }

    // Si baja a 0, mostramos icono de mute
    if (newVolume === 0) {
      setIsMuted(true)
    }
  }

  // 6. Reset button when video ends
  const handleVideoEnded = () => {
    handleLoadedMetadata()
    setIsPlaying(false)
    setProgress(0)
  }

  // 7. Handle full screen mode
  const toggleFullScreen = async () => {
    const playerElement = playerContainerRef.current
    const doc = document

    if (!isFullScreen) {
      // Entrar en FullScreen
      if (!playerElement) return
      if (playerElement.requestFullscreen) {
        await playerElement.requestFullscreen()
      } else if (playerElement.webkitRequestFullscreen) {
        /* Safari */
        await playerElement.webkitRequestFullscreen()
      } else if (playerElement.msRequestFullscreen) {
        /* IE11 */
        await playerElement.msRequestFullscreen()
      }
    } else {
      // Salir de FullScreen
      if (doc.exitFullscreen) {
        await doc.exitFullscreen()
      } else if (doc.webkitExitFullscreen) {
        /* Safari */
        await doc.webkitExitFullscreen()
      } else if (doc.msExitFullscreen) {
        /* IE11 */
        await doc.msExitFullscreen()
      }
    }
  }

  // Detect changes in FullScreen (e.g., if user presses ESC)
  useEffect(() => {
    const doc = document

    const handleFullScreenChange = () => {
      setIsFullScreen(
        !!(
          doc.fullscreenElement ||
          doc.webkitFullscreenElement ||
          doc.msFullscreenElement
        )
      )
    }

    document.addEventListener("fullscreenchange", handleFullScreenChange)
    // Support for Safari/IE
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange)
    document.addEventListener("msfullscreenchange", handleFullScreenChange)

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange)
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullScreenChange
      )
      document.removeEventListener("msfullscreenchange", handleFullScreenChange)
    }
  }, [])

  return {
    videoRef,
    playerContainerRef,
    isPlaying,
    togglePlay,
    handleTimeUpdate,
    handleProgressChange,
    handleLoadedMetadata,
    toggleMute,
    handleVideoEnded,
    toggleFullScreen,
    handleVolumeChange,
    isMuted,
    isFullScreen,
    progress,
    currentTime,
    duration,
    volume
  }
}

export default useCustomVideoPlayer
